/**
 * The parameter inspector (requirements 13.2, 13.3, 13.4).
 *
 * For one selected component or channel: its effective parameters, with the
 * designer's overrides marked against the template's values; each numeric field
 * showing its unit, which can be changed, and accepting a typed unit ("0.2 mm");
 * and every finding about a field placed against that field.
 *
 * Values leave this component as canonical SI (invariant 5). A value that
 * cannot be stored is not dispatched: the field keeps what was typed and says
 * why, which is what "shown against that field rather than as a general error"
 * asks for.
 */
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import type {
  EdgeDocument,
  Finding,
  NodeDocument,
  ParameterDeclaration,
  TemplateDocument,
} from "../design/document";
import { checkTemplateSwap, lockingInstance, type EditorAction, type EditorState } from "../editor";
import { ResizableHead, type Column } from "./ColumnWidths";
import type { WorkspaceLibrary } from "./library";
import { countedEnd, countsFromStart, drawnScene, isMountable, shownDistance, storedDistance } from "../editor/mounts";
import {
  describeBounds,
  effectiveParameters,
  fieldFindings,
  findingElement,
  findingParameter,
  isNumeric,
  parameterLabel,
  sameValue,
  validateParameter,
  type ParameterRow,
} from "./parameters";
import { formatNumber, formatQuantity, fromCanonical, liquidUnitFor, parseQuantity, preferredUnit } from "./units";
import { Glyph } from "../render/glyphs";
import { symbolTransform } from "../render/SvgRenderer";

import { useDesignLiquidUnits } from "./DisplayUnitsContext";
import { billOfMaterials, bomCuts, bomQuantity, type BomSide } from "./billOfMaterials";

export interface InspectorProps {
  state: EditorState;
  library: WorkspaceLibrary;
  /** Every current finding; the inspector picks out those for the selection. */
  findings: readonly Finding[];
  dispatch: (action: EditorAction) => void;
  /** The block operations the workspace performs through the server (D20). */
  blocks?: BlockActions;
  /** A bill of materials line hovered: its parts, to pick out on the drawing; null on leaving. */
  onSpotlight?: (refs: readonly string[] | null) => void;
}

export interface BlockActions {
  detach: (instance: string) => void;
  openDefinition: (instance: string) => void;
  update: (instance: string) => void;
  /** The newer version number available to an instance, or null. */
  newerVersion: (instance: string) => number | null;
}

/**
 * Why a locked field cannot be changed, and the way out: detaching (D20,
 * requirement 8.9). The offer is made where the edit was attempted.
 */
function LockedNote({ instance, what, blocks }: { instance: string; what: string; blocks?: BlockActions }) {
  return (
    <p className="ws-muted ws-small ws-locked">
      Locked in block {instance}: detach it to {what}.{" "}
      {blocks && (
        <button type="button" className="ws-link" onClick={() => blocks.detach(instance)}>
          Detach {instance}…
        </button>
      )}
    </p>
  );
}

/**
 * Findings under a field. What the designer just typed and could not be
 * stored is `refused`, and reads as a correction. What a check found is a
 * note: the value is stored, and the check says what the profile thinks of it.
 */
function FieldFindings({
  id,
  findings,
  refused,
}: {
  id: string;
  findings: readonly Finding[];
  refused?: Finding | null;
}) {
  if (findings.length === 0 && !refused) return null;
  return (
    <ul className="ws-field-findings" id={id}>
      {refused && <li className="ws-field-refused">{refused.message}</li>}
      {findings.map((finding, index) => (
        <li key={`${finding.code}-${index}`} className={`ws-field-note ws-note-${finding.severity}`}>
          {finding.message}
        </li>
      ))}
    </ul>
  );
}

/**
 * One parameter. `local` holds a value the designer typed that could not be
 * stored, with its finding; it clears when a valid value is committed or the
 * stored value changes underneath (undo, another edit).
 */
export function ParameterField({
  refId,
  row,
  locked,
  serverFindings,
  onCommit,
  note,
  bulk = false,
}: {
  refId: string;
  row: ParameterRow;
  locked: string | null;
  serverFindings: readonly Finding[];
  onCommit: (value: unknown) => void;
  /** Where a changed value comes from, shown before "default …" (placement defaults, D36). */
  note?: string | null;
  /**
   * Several elements at once: `row.effective` is their shared value, or
   * undefined where they differ ("mixed"). A value is committed as typed; each
   * element drops its override where it is its own template's default.
   */
  bulk?: boolean;
}) {
  const { declaration } = row;
  const label = parameterLabel(row.key, declaration);
  const findingsId = useId();
  const canonical = declaration.unit ?? "dimensionless";
  // A volume, flow or (once chosen) length is shown in the design's unit (D49). Anything else is
  // chosen from the default, not the current value, so it stays put while the
  // designer edits.
  const designUnit = liquidUnitFor(canonical, useDesignLiquidUnits());
  const [unit, setUnit] = useState(
    () =>
      designUnit ??
      preferredUnit(canonical, typeof row.defaultValue === "number" ? row.defaultValue : (row.effective as number)),
  );
  // The design's setting changed: follow it.
  useEffect(() => {
    if (designUnit) setUnit(designUnit);
  }, [designUnit]);
  const mixed = bulk && row.effective === undefined;
  const shown = (value: unknown) =>
    typeof value === "number" && isNumeric(declaration) ? formatNumber(fromCanonical(value, unit)) : String(value ?? "");
  const [text, setText] = useState(() => shown(row.effective));
  const [local, setLocal] = useState<Finding | null>(null);

  // Follow the document: undo, redo or an edit elsewhere replaces what is shown.
  useEffect(() => {
    setText(shown(row.effective));
    setLocal(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.effective]);
  // A change of unit redraws the value in it, but keeps a refusal: typing
  // "5 mm" where µm was shown switches the unit and must still say why 5 mm
  // was not stored.
  const unitShown = useRef(unit);
  useEffect(() => {
    if (unitShown.current === unit) return;
    unitShown.current = unit;
    if (local === null) setText(shown(row.effective));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit]);

  const commitValue = (value: unknown) => {
    const refusal = validateParameter(refId, declaration, value);
    if (refusal) {
      setLocal(refusal);
      return;
    }
    setLocal(null);
    if (!mixed && sameValue(value, row.effective)) return;
    if (bulk) return onCommit(value);
    // Back to the template's value is no longer an override (requirement 4.2).
    onCommit(sameValue(value, row.defaultValue) ? null : value);
  };

  const commitText = () => {
    // Left blank over mixed values: nothing is changed.
    if (mixed && text.trim() === "") return setLocal(null);
    if (!isNumeric(declaration)) {
      commitValue(text);
      return;
    }
    if (text.trim() === shown(row.effective)) {
      setLocal(null);
      return;
    }
    const parsed = parseQuantity(text, canonical, unit);
    if (!parsed.ok) {
      setLocal({ code: "invalid_unit", message: parsed.reason, severity: "error", path: refId, detail: { key: row.key } });
      return;
    }
    if (parsed.unit !== unit) setUnit(parsed.unit);
    commitValue(parsed.value);
  };

  const findings = [...(local ? [local] : []), ...serverFindings];
  // Only a value that could not be stored marks the field invalid; a check
  // finding about a stored value is a note beside it.
  const invalid = local !== null;
  const describedBy = findings.length > 0 ? findingsId : undefined;
  const disabled = locked !== null;
  const bounds = isNumeric(declaration) ? describeBounds(declaration) : null;
  const defaultText =
    row.defaultValue === null || row.defaultValue === undefined
      ? "none"
      : typeof row.defaultValue === "number" && isNumeric(declaration)
        ? formatQuantity(row.defaultValue, canonical, unit)
        : String(row.defaultValue);

  let control;
  if (declaration.value_type === "bool") {
    control = (
      <input
        type="checkbox"
        aria-label={label}
        checked={row.effective === true}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={(event) => commitValue(event.target.checked)}
      />
    );
  } else if (declaration.value_type === "enum") {
    control = (
      <select
        aria-label={label}
        value={String(row.effective ?? "")}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={(event) => event.target.value !== "" && commitValue(event.target.value)}
      >
        {mixed && <option value="">mixed</option>}
        {(declaration.enum_values ?? []).map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  } else {
    control = (
      <input
          className="ws-param-input"
          aria-label={label}
          value={text}
          placeholder={mixed ? "mixed" : undefined}
          disabled={disabled}
          inputMode={isNumeric(declaration) ? "decimal" : undefined}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          title={bounds ?? undefined}
          onChange={(event) => setText(event.target.value)}
          onBlur={commitText}
          onKeyDown={(event) => {
            if (event.key === "Enter") commitText();
            if (event.key === "Escape") {
              setText(shown(row.effective));
              setLocal(null);
            }
          }}
        />
    );
  }

  // One row: the parameter, the value, its unit. An override is marked beside
  // the name with a way back to the template's value, whose default is on
  // hover; the bounds are on hover of the value. Only a finding takes a line
  // of its own beneath.
  const unitText = isNumeric(declaration) ? unit.label : "";
  const resetTitle = `${note ? `${note} · ` : ""}${bulk ? "Back to each template's default" : `Default ${defaultText}`}`;
  return (
    <tbody className={row.overridden ? "ws-param ws-overridden" : "ws-param"} data-parameter={row.key}>
      <tr>
        <th scope="row" title={declaration.description || undefined}>
          {label}
          {row.overridden && (
            <span className="ws-override-dot" title={resetTitle}>
              <span className="visually-hidden"> (overridden, {resetTitle.toLowerCase()})</span>
            </span>
          )}
          {row.overridden && !disabled && (
            <button
              type="button"
              className="ws-reset"
              title={`${resetTitle}: reset`}
              aria-label={`Reset ${label} to the template default`}
              onClick={() => onCommit(null)}
            >
              ↺
            </button>
          )}
        </th>
        <td className="ws-param-value">{control}</td>
        <td className="ws-unit">{unitText}</td>
      </tr>
      {(findings.length > 0 || (note && row.overridden)) && (
        <tr className="ws-param-detail">
          <td colSpan={3}>
            {/* Where a default comes from matters when setting defaults (D36), so it is said. */}
            {note && row.overridden && <div className="ws-range">{resetTitle}</div>}
            <FieldFindings id={findingsId} findings={serverFindings} refused={local} />
          </td>
        </tr>
      )}
    </tbody>
  );
}

const PARAMETER_COLUMNS: readonly Column[] = [
  { heading: "Parameter", width: 0.4 },
  { heading: "Value", width: 0.42 },
  { heading: "Unit", width: 0.18 },
];

/** The table's column headings, shared by the inspector and the defaults dialog; the widths are this person's. */
export function ParameterTableHead() {
  return <ResizableHead table="parameters" columns={PARAMETER_COLUMNS} />;
}

const PORT_COLUMNS: readonly Column[] = [
  { heading: "Port", width: 0.28 },
  { heading: "To", width: 0.36 },
  { heading: "External", width: 0.18 },
  { heading: "Dead end", width: 0.18 },
];

function ParameterTable({
  refId,
  element,
  template,
  locked,
  findings,
  dispatch,
  children,
}: {
  refId: string;
  element: NodeDocument | EdgeDocument;
  template: TemplateDocument | undefined;
  locked: string | null;
  findings: readonly Finding[];
  dispatch: (action: EditorAction) => void;
  /** Rows before the parameters: where a mounted part sits. */
  children?: ReactNode;
}) {
  const rows = effectiveParameters(element, template);
  if (!template) return <p className="ws-muted ws-small">The pinned template could not be loaded, so its parameters are unknown.</p>;
  if (rows.length === 0 && !children) return <p className="ws-muted ws-small">This template declares no parameters.</p>;
  return (
    <table className="ws-params ws-grid ws-resizable" aria-label="Parameters">
      <ParameterTableHead />
      {children}
      {rows.map((row) => (
        <ParameterField
          // Keyed by element too, so switching selection starts each field afresh.
          key={`${refId}:${row.key}`}
          refId={refId}
          row={row}
          locked={locked}
          serverFindings={fieldFindings(findings, refId, row.key)}
          onCommit={(value) => dispatch({ type: "EDIT_PARAMETER", ref: refId, key: row.key, value })}
        />
      ))}
    </table>
  );
}

/** Findings about the element that are not about one of its fields. */
function elementLevelFindings(findings: readonly Finding[], ref: string, declared: readonly ParameterDeclaration[]) {
  const keys = new Set(declared.map((declaration) => declaration.key));
  return findings.filter((finding) => {
    if (findingElement(finding) !== ref) return false;
    const parameter = findingParameter(finding);
    return parameter === null || !keys.has(parameter);
  });
}

function LabelField({ node, disabled, dispatch }: { node: NodeDocument; disabled: boolean; dispatch: InspectorProps["dispatch"] }) {
  const [value, setValue] = useState(node.label ?? "");
  useEffect(() => setValue(node.label ?? ""), [node.label]);
  const commit = () => {
    if (value !== (node.label ?? "")) dispatch({ type: "SET_LABEL", ref: node.local_id, label: value });
  };
  return (
    <input
      className="ws-label-input ws-title-input"
      value={value}
      aria-label="Label"
      placeholder={node.local_id}
      title={`Label; its identifier is ${node.local_id}`}
      disabled={disabled}
      onChange={(event) => setValue(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => event.key === "Enter" && commit()}
    />
  );
}

/** What an element is made from, for the icon's tooltip: "Bag · v2 (pinned) · reservoir_3". */
function templateText(library: WorkspaceLibrary, versionId: string | null | undefined): string {
  if (!versionId) return "No template";
  const entry = library.versions[versionId];
  if (!entry) return `Template ${versionId.slice(0, 8)} unavailable`;
  return `${entry.name} · v${entry.number} (pinned)${entry.projectScoped ? " · project variant" : ""}`;
}

/** A newer version of the pinned template, worth saying out loud; null otherwise. */
function newerVersion(library: WorkspaceLibrary, versionId: string | null | undefined): number | null {
  const entry = versionId ? library.versions[versionId] : undefined;
  if (!entry || entry.latestNumber === null || entry.latestVersionId === versionId) return null;
  return entry.latestNumber > entry.number ? entry.latestNumber : null;
}

/**
 * That a newer version of the template is out, and the update to it, in
 * place: the same identifier, label, connections and the values set on it,
 * so what refers to it (a script, a check's spec) still does. Refused, with
 * the reason, where the new version would strand a line or it is locked in a
 * block.
 */
function TemplateUpdate({
  element,
  kind,
  library,
  state,
  locked,
  dispatch,
}: {
  element: NodeDocument | EdgeDocument;
  kind: "node" | "edge";
  library: WorkspaceLibrary;
  state: EditorState;
  locked: string | null;
  dispatch: (action: EditorAction) => void;
}) {
  const newer = newerVersion(library, element.template_version_id);
  if (newer === null && !locked) return null;
  const latest = element.template_version_id ? library.versions[element.template_version_id]?.latestVersionId : null;
  const loaded = latest ? library.scene.templates[latest] !== undefined : false;
  const refusal =
    newer === null || !latest
      ? null
      : !loaded
        ? `v${newer} is not loaded yet`
        : kind === "node"
          ? (checkTemplateSwap(state.document, state.library, element.local_id, latest)?.message ?? null)
          : locked
            ? `locked in ${locked}; detach it to update`
            : null;
  return (
    <p className="ws-muted ws-small ws-template-update">
      {newer !== null && `v${newer} of its template is available`}
      {newer !== null && latest && (
        <>
          {" "}
          <button
            type="button"
            className="ws-link"
            disabled={refusal !== null}
            title={refusal ?? "Same component and connections; the values you set carry over"}
            onClick={() =>
              dispatch(
                kind === "node"
                  ? { type: "SET_NODE_TEMPLATE", ref: element.local_id, templateVersionId: latest }
                  : { type: "SET_EDGE_TEMPLATE", ref: element.local_id, templateVersionId: latest },
              )
            }
          >
            Update to v{newer}
          </button>
        </>
      )}
      {newer !== null && locked && " · "}
      {locked && `locked in ${locked}`}
    </p>
  );
}

/** The selected component's symbol, turned and flipped as it is drawn. */
function SymbolIcon({ state, library, node, title }: { state: EditorState; library: WorkspaceLibrary; node: NodeDocument; title: string }) {
  const drawn = drawnScene(state.document, library.scene).nodes.find((candidate) => candidate.ref === node.local_id);
  const template = node.template_version_id ? library.scene.templates[node.template_version_id] : undefined;
  const size = drawn?.size ?? { width: template?.symbol.width ?? 40, height: template?.symbol.height ?? 32 };
  const rotation = drawn?.rotation ?? node.schematic_position?.rotation ?? 0;
  const mirrored = drawn?.mirrored ?? node.schematic_position?.mirrored ?? false;
  // Room for any rotation: the diagonal of the symbol, and a margin.
  const reach = Math.hypot(size.width, size.height) / 2 + 2;
  return (
    <svg className="ws-title-icon" viewBox={`${-reach} ${-reach} ${2 * reach} ${2 * reach}`} role="img" aria-label={title}>
      <title>{title}</title>
      <g transform={symbolTransform({ x: 0, y: 0 }, rotation, mirrored)}>
        <Glyph name={drawn?.glyph ?? template?.symbol.glyph ?? "unknown"} size={size} />
      </g>
    </svg>
  );
}

/**
 * Where a mounted part sits on its tube (D40): a hydraulic distance, typed in
 * from the left end of a mostly horizontal tube or the top of a mostly
 * vertical one. Stored from the tube's own start, so redrawing the tube never
 * moves the part; this is where the two are converted.
 */
function MountSection({ node, state, library, dispatch }: InspectorProps & { node: NodeDocument }) {
  const mount = node.mount!;
  const edge = state.document.edges?.find((candidate) => candidate.local_id === mount.edge);
  const line = drawnScene(state.document, library.scene).edges.find((candidate) => candidate.ref === mount.edge);
  const length = edge?.hydraulic?.length ?? null;
  const fromStart = line ? countsFromStart(line) : true;
  const end = line ? countedEnd(line) : "left";
  const shown = shownDistance(mount.distance, length, fromStart);
  const unit = useDesignLiquidUnits()?.length ?? preferredUnit("meter", length ?? shown);
  const format = (value: number) => formatNumber(fromCanonical(value, unit));
  const [text, setText] = useState(format(shown));
  const [problem, setProblem] = useState<string | null>(null);
  const fieldId = useId();
  const helpId = useId();
  useEffect(() => {
    setText(format(shown));
    setProblem(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, unit.label]);
  const commit = () => {
    if (text.trim() === format(shown)) return;
    const parsed = parseQuantity(text, "meter", unit);
    if (!parsed.ok) {
      setProblem(parsed.reason);
      return;
    }
    if (parsed.value < 0 || (length !== null && parsed.value > length)) {
      setProblem(`Between 0 and ${length !== null ? formatQuantity(length, "meter", unit) : "the tube's length"}`);
      return;
    }
    setProblem(null);
    dispatch({ type: "SET_MOUNT_DISTANCE", ref: node.local_id, distance: storedDistance(parsed.value, length, fromStart) });
  };
  return (
    <tbody className="ws-param" data-parameter="mount">
      <tr>
        <th scope="row">
          <label htmlFor={fieldId} title={`Mounted on ${mount.edge}${length !== null ? `, ${formatQuantity(length, "meter", unit)} long` : ", which has no length yet"}. Along the tube, as it is cut: dragging the symbol moves only the drawing.`}>
            Distance from the {end} end
          </label>
        </th>
        <td className="ws-param-value">
          <input
            id={fieldId}
            className="ws-param-input"
            inputMode="decimal"
            value={text}
            aria-invalid={problem !== null || undefined}
            aria-describedby={problem ? helpId : undefined}
            onChange={(event) => setText(event.target.value)}
            onBlur={commit}
            onKeyDown={(event) => event.key === "Enter" && commit()}
          />
        </td>
        <td className="ws-unit">{unit.label}</td>
      </tr>
      {problem && (
        <tr className="ws-param-detail">
          <td colSpan={3}>
            <span id={helpId} className="ws-small ws-field-refused">
              {problem}
            </span>
          </td>
        </tr>
      )}
    </tbody>
  );
}

/** A component's ports: connected to what, and whether each is an interface or closed on purpose. */
function PortTable({ node, state, locked, dispatch }: { node: NodeDocument; state: EditorState; locked: string | null; dispatch: InspectorProps["dispatch"] }) {
  const edges = state.document.edges ?? [];
  const ports = node.ports ?? [];
  if (ports.length === 0) return null;
  return (
    <table className="ws-params ws-grid ws-port-table ws-resizable" aria-label="Ports">
      <ResizableHead table="ports" columns={PORT_COLUMNS} />
      <tbody>
        {ports.map((port) => {
          const ref = `${node.local_id}.${port.local_id}`;
          const edge = edges.find((candidate) => candidate.from_port === ref || candidate.to_port === ref);
          const name = port.name || port.local_id;
          return (
            <tr key={ref}>
              <th scope="row" title={`${port.local_id}${port.interface ? ` · ${port.interface}` : ""}`}>
                {name}
              </th>
              <td className="ws-muted">{edge ? edge.local_id : node.mount && port.kind !== "instrument" ? node.mount.edge : "—"}</td>
              <td className="ws-check-cell">
                <input
                  type="checkbox"
                  aria-label={`${name} external`}
                  checked={port.is_external ?? false}
                  disabled={locked !== null}
                  onChange={(event) => dispatch({ type: "SET_PORT_EXTERNAL", ref, external: event.target.checked })}
                />
              </td>
              <td className="ws-check-cell" title={edge && !port.dead_end ? `Connected by ${edge.local_id}` : "Closed on purpose: nothing connects here"}>
                <input
                  type="checkbox"
                  aria-label={`${name} dead end`}
                  checked={port.dead_end ?? false}
                  disabled={locked !== null || (edge !== undefined && !port.dead_end)}
                  onChange={(event) => dispatch({ type: "SET_PORT_DEAD_END", ref, deadEnd: event.target.checked })}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function NodeInspector(props: InspectorProps & { node: NodeDocument }) {
  const { node, state, library, findings, dispatch } = props;
  const design = state.document;
  const template = node.template_version_id ? library.scene.templates[node.template_version_id] : undefined;
  const locked = lockingInstance(design, node.local_id)?.local_id ?? null;
  const general = elementLevelFindings(findings, node.local_id, template?.parameters ?? []);
  return (
    <>
      {/* Just the name, and the symbol as it is drawn; what it is made from is on the symbol's hover. */}
      <div className="ws-inspector-head ws-title">
        <LabelField node={node} disabled={false} dispatch={dispatch} />
        <SymbolIcon state={state} library={library} node={node} title={`${templateText(library, node.template_version_id)} · ${node.local_id}`} />
      </div>
      <TemplateUpdate element={node} kind="node" library={library} state={state} locked={locked} dispatch={dispatch} />
      <FieldFindings id={`${node.local_id}-findings`} findings={general} />
      {!node.mount && state.designKind === "tubing_set" && isMountable(node, template) && (
        <p className="ws-small">
          <button type="button" onClick={() => dispatch({ type: "MOUNT_ON_LINE", ref: node.local_id })} title="It sits on the tube: join its two tubes into one it is mounted on">
            Mount on line
          </button>
        </p>
      )}
      {locked && <LockedNote instance={locked} what="change parameters" blocks={props.blocks} />}
      <ParameterTable refId={node.local_id} element={node} template={template} locked={locked} findings={findings} dispatch={dispatch}>
        {node.mount && <MountSection {...props} node={node} />}
      </ParameterTable>
      <PortTable node={node} state={state} locked={locked} dispatch={dispatch} />
    </>
  );
}

function EdgeInspector(props: InspectorProps & { edge: EdgeDocument }) {
  const { edge, state, library, findings, dispatch } = props;
  const template = edge.template_version_id ? library.scene.templates[edge.template_version_id] : undefined;
  const locked = lockingInstance(state.document, edge.local_id)?.local_id ?? null;
  const general = elementLevelFindings(findings, edge.local_id, template?.parameters ?? []);
  return (
    <>
      <div className="ws-inspector-head ws-title">
        <h2 title={`${templateText(library, edge.template_version_id)} · ${edge.from_port} → ${edge.to_port}. Its length is the cut length, not the drawn one.`}>
          {edge.local_id}
        </h2>
      </div>
      <TemplateUpdate element={edge} kind="edge" library={library} state={state} locked={locked} dispatch={dispatch} />
      <FieldFindings id={`${edge.local_id}-findings`} findings={general} />
      {locked && <LockedNote instance={locked} what="change its dimensions" blocks={props.blocks} />}
      <ParameterTable refId={edge.local_id} element={edge} template={template} locked={locked} findings={findings} dispatch={dispatch} />
    </>
  );
}

/**
 * Several components or lines selected: the parameters they all have, each
 * editable for all of them at once (one edit, one undo). A value they share
 * is shown; where they differ the field says "mixed", and leaving it blank
 * changes nothing.
 */
function BulkInspector({ refs, state, library, dispatch }: InspectorProps & { refs: readonly string[] }) {
  const design = state.document;
  const elements = refs
    .map((ref) => design.nodes?.find((node) => node.local_id === ref) ?? design.edges?.find((edge) => edge.local_id === ref))
    .filter((element): element is NodeDocument | EdgeDocument => element !== undefined);
  const templates = elements.map((element) => (element.template_version_id ? library.scene.templates[element.template_version_id] : undefined));
  const perElement = elements.map((element, index) => effectiveParameters(element, templates[index]));
  const compatible = (a: ParameterDeclaration, b: ParameterDeclaration) =>
    a.value_type === b.value_type &&
    (a.unit ?? "") === (b.unit ?? "") &&
    JSON.stringify(a.enum_values ?? null) === JSON.stringify(b.enum_values ?? null);
  const shared: ParameterRow[] =
    elements.length === 0 || templates.some((template) => !template)
      ? []
      : perElement[0]!.flatMap((first) => {
          const matching = perElement.map((rows) => rows.find((row) => row.key === first.key && compatible(row.declaration, first.declaration)));
          if (matching.some((row) => row === undefined)) return [];
          const rows = matching as ParameterRow[];
          const same = rows.every((row) => sameValue(row.effective, first.effective));
          return [
            {
              key: first.key,
              declaration: first.declaration,
              effective: same ? first.effective : undefined,
              defaultValue: rows.every((row) => sameValue(row.defaultValue, first.defaultValue)) ? first.defaultValue : null,
              overridden: rows.some((row) => row.overridden),
            },
          ];
        });
  const locked = elements.map((element) => lockingInstance(design, element.local_id)?.local_id).find(Boolean) ?? null;
  const kinds = new Map<string, number>();
  elements.forEach((element, index) => {
    const name = element.template_version_id ? (library.versions[element.template_version_id]?.name ?? templates[index]?.role) : undefined;
    const key = name ?? ("from_port" in element ? "line" : element.role);
    kinds.set(key, (kinds.get(key) ?? 0) + 1);
  });
  const others = refs.length - elements.length;
  return (
    <>
      <div className="ws-inspector-head ws-title">
        <h2>{refs.length} selected</h2>
      </div>
      <p className="ws-muted ws-small">
        {[...kinds].map(([name, count]) => `${count} ${name}`).join(", ")}
        {others > 0 ? `${kinds.size > 0 ? ", " : ""}${others} other` : ""}
      </p>
      {locked && <LockedNote instance={locked} what="change parameters" blocks={undefined} />}
      {shared.length === 0 ? (
        <p className="ws-muted ws-small">
          {elements.length === 0 ? "Nothing selected has parameters." : "They have no parameters in common to edit together."}
        </p>
      ) : (
        <table className="ws-params ws-grid" aria-label="Shared parameters">
          <ParameterTableHead />
          {shared.map((row) => (
            <ParameterField
              key={`${refs.join(",")}:${row.key}`}
              refId={`${elements.length} selected`}
              row={row}
              locked={locked}
              serverFindings={[]}
              bulk
              onCommit={(value) => dispatch({ type: "EDIT_PARAMETERS", refs: elements.map((element) => element.local_id), key: row.key, value })}
            />
          ))}
        </table>
      )}
    </>
  );
}

/**
 * With one side shown on its own (the single-use set, or what is outside it),
 * a bill of materials for that side (`billOfMaterials`).
 */
function BillOfMaterials({
  state,
  library,
  onSpotlight,
}: {
  state: EditorState;
  library: WorkspaceLibrary;
  onSpotlight?: (refs: readonly string[] | null) => void;
}) {
  const side = state.annotations.wash as BomSide;
  const liquid = useDesignLiquidUnits();
  // Nothing stays picked out once the list is gone.
  useEffect(() => () => onSpotlight?.(null), [onSpotlight]);
  const { lines: rows, tubeUnit } = billOfMaterials(state.document, library, side, liquid);
  const title = side === "inside" ? "Single-use set" : "Outside the set";
  return (
    <>
      <div className="ws-inspector-head ws-title">
        <h2>{title}</h2>
      </div>
      <p className="ws-muted ws-small">
        Bill of materials for what is shown. {side === "inside" ? "The instrument's parts are left out." : "Includes the instrument's clamp-on parts."}
      </p>
      {rows.length === 0 ? (
        <p className="ws-muted ws-small">Nothing on this side.</p>
      ) : (
        <table className="ws-params ws-grid ws-bom" aria-label="Bill of materials">
          <thead>
            <tr>
              <th scope="col">Item</th>
              <th scope="col">Qty</th>
              <th scope="col">Unit</th>
            </tr>
          </thead>
          <tbody onMouseLeave={() => onSpotlight?.(null)}>
            {rows.map((row) => {
              const { quantity, unit } = bomQuantity(row, tubeUnit);
              const cuts = bomCuts(row);
              return (
                <tr
                  key={row.item}
                  title={`On the drawing: ${row.refs.join(", ")}`}
                  onMouseEnter={() => onSpotlight?.(row.refs)}
                >
                  <th scope="row">
                    {row.item}
                    {cuts && <span className="ws-muted"> · {cuts}</span>}
                  </th>
                  <td className="ws-bom-count">{quantity}</td>
                  <td className="ws-unit">{unit}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}

function BlockSummary({
  state,
  instanceId,
  library,
  dispatch,
  blocks,
}: {
  state: EditorState;
  instanceId: string;
  library: WorkspaceLibrary;
  dispatch: (action: EditorAction) => void;
  blocks?: BlockActions;
}) {
  const instance = state.document.group_instances?.find((candidate) => candidate.local_id === instanceId);
  if (!instance) return null;
  const entry = library.versions[instance.group_definition_version_id];
  const collapsed = state.collapsed.has(instanceId);
  const newer = blocks?.newerVersion(instanceId) ?? null;
  return (
    <div className="ws-inspector-head">
      <span className="ws-muted">block instance</span>
      <h2>{instance.local_id}</h2>
      <div className="ws-muted ws-small">
        {entry ? `${entry.name} · v${entry.number}` : "Definition unavailable"} · {instance.member_local_ids?.length ?? 0}{" "}
        members{instance.detached ? ", detached" : ", locked"}
        {newer !== null ? ` · v${newer} available` : ""}
      </div>
      <div className="ws-row ws-block-actions">
        <button
          type="button"
          onClick={() => dispatch({ type: "SET_COLLAPSED", instance: instanceId, collapsed: !collapsed })}
        >
          {collapsed ? "Expand" : "Collapse"}
        </button>
        {blocks && (
          <>
            <button type="button" onClick={() => blocks.openDefinition(instanceId)} disabled={!entry}>
              Open definition
            </button>
            {newer !== null && (
              <button type="button" onClick={() => blocks.update(instanceId)}>
                Update to v{newer}…
              </button>
            )}
            <button type="button" onClick={() => blocks.detach(instanceId)}>
              Detach…
            </button>
          </>
        )}
      </div>
      <p className="ws-muted ws-small">
        Collapsing changes the view only. Members are locked to the definition; detaching makes them ordinary
        components and ends updates for this instance.
      </p>
      <h3>Members</h3>
      <ul className="ws-ports">
        {instance.member_local_ids?.map((member) => (
          <li key={member}>
            <code>{member}</code>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Inspector(props: InspectorProps) {
  const { state, library } = props;
  const selected = useMemo(() => [...state.selection], [state.selection]);
  const only = selected.length === 1 ? selected[0]! : null;
  const node = only ? state.document.nodes?.find((candidate) => candidate.local_id === only) : undefined;
  const edge = only && !node ? state.document.edges?.find((candidate) => candidate.local_id === only) : undefined;
  const isolating = Boolean(state.annotations.isolate) && state.annotations.wash !== "none";

  let body;
  if (node) body = <NodeInspector {...props} node={node} />;
  else if (edge) body = <EdgeInspector {...props} edge={edge} />;
  else if (only)
    body = (
      <BlockSummary state={state} instanceId={only} library={library} dispatch={props.dispatch} blocks={props.blocks} />
    );
  else if (selected.length > 1) body = <BulkInspector {...props} refs={selected} />;
  else if (isolating) body = <BillOfMaterials state={state} library={library} onSpotlight={props.onSpotlight} />;
  else body = <p className="ws-muted">Select a component or line to inspect it; select several to edit what they share.</p>;

  return (
    <aside className="ws-inspector" aria-label="Inspector">
      {body}
    </aside>
  );
}
